import React, { useState } from "react";
import "../../../css/ViewSlotTemplateModal.css";
import EditSlotTemplateModal from "./EditSlotTemplateModal";
// ✅ Import the new components and api instance
import ConfirmationModal from "./ConfirmationModal";
import api from "../../../api/axiosConfig";
import { showToast } from "../../Shared/Toaster";

interface Slot {
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface SlotTemplate {
  _id: string;
  name: string;
  created_on: string;
  disabled_on?: string;
  slots?: Slot[];
}

interface Props {
  template: SlotTemplate;
  onClose: () => void;
  onUpdate: () => void;
}

const ViewSlotTemplateModal: React.FC<Props> = ({
  template,
  onClose,
  onUpdate,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // ✅ State to control the confirmation modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleUpdateSuccess = () => {
    onUpdate(); // Refresh the table data
    onClose(); // Close the view modal
  };

  /**
   * ✅ Handles the disable action after confirmation.
   * Sends a DELETE request to the API to disable the template.
   */
  const handleDisableConfirm = async () => {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      console.error("Authentication token not found.");
      return;
    }

    try {
      // Send DELETE request to the endpoint
      await api.appointment_api.delete(`/api/slot-templates/${template._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // On success, trigger the parent's update function and close all modals
      showToast("Slot template disabled successfully ✅", "success");
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error("Failed to disable the slot template:", error);
      showToast(error.response?.data?.message || "Failed to disable slot template ❌", "error");
    } finally {
      // Close the confirmation modal regardless of outcome
      setIsConfirmModalOpen(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content view-slot-template" onClick={(e) => e.stopPropagation()}>
          <h2>View Slot Template</h2>

          <p>
            <strong>Slot Name:</strong> {template.name}
          </p>
          <p>
            <strong>Created On:</strong>{" "}
            {new Date(template.created_on).toLocaleDateString()}
          </p>
          <p>
            <strong>Disabled On:</strong>{" "}
            {template.disabled_on
              ? new Date(template.disabled_on).toLocaleDateString()
              : "-"}
          </p>

          <table className="slot-table-details">
            <thead>
              <tr>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Slot Type</th>
              </tr>
            </thead>
            <tbody>
              {template.slots?.map((slot, idx) => (
                <tr key={idx}>
                  <td>{slot.start_time}</td>
                  <td>{slot.end_time}</td>
                  <td>
                    <span
                      className={`badge type ${slot.slot_type
                        .replace(" ", "-")
                        .toLowerCase()}`}
                    >
                      {slot.slot_type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="modal-actions">
            <button onClick={onClose} className="btn">
              Close
            </button>
            <button
              className="btn edit"
              onClick={() => setIsEditModalOpen(true)}
            >
              Edit
            </button>
            {/* ✅ Open the confirmation modal on click and disable if already disabled */}
            <button
              className="btn disable"
              onClick={() => setIsConfirmModalOpen(true)}
              disabled={!!template.disabled_on} // The button is disabled if 'disabled_on' has a value
            >
              Disable
            </button>
          </div>
        </div>
      </div>

      {/* Conditionally render the Edit modal */}
      {isEditModalOpen && (
        <EditSlotTemplateModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          template={template}
          onUpdateSuccess={handleUpdateSuccess}
        />
      )}

      {/* ✅ Conditionally render the Confirmation modal */}
      {isConfirmModalOpen && (
        <ConfirmationModal
          message={`Are you sure you want to disable the "${template.name}" template?`}
          onConfirm={handleDisableConfirm}
          onCancel={() => setIsConfirmModalOpen(false)}
        />
      )}
    </>
  );
};

export default ViewSlotTemplateModal;